import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';

import { storeService } from '../../features/store-market/services/storeService';
import { StoreFormData, ProductData } from '../../features/store-market/types/store.types';

const StorePublicPage: React.FC = () => {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const [store, setStore] = useState<StoreFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (storeSlug) {
      loadStore();
    }
  }, [storeSlug]);

  const loadStore = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storeService.getStoreBySlug(storeSlug!);
      setStore(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar loja:', error);
      setError(error.message || 'Loja não encontrada');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Carregando loja...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !store) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Loja não encontrada'}
        </Alert>
      </Container>
    );
  }

  const primaryColor = store.customTheme?.primaryColor || '#1976d2';
  const secondaryColor = store.customTheme?.secondaryColor || '#dc004e';
  const fontFamily = store.customTheme?.fontFamily || 'Roboto';

  return (
    <Box sx={{ fontFamily }}>
      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs>
          <Link href="/" underline="hover" color="inherit">
            Início
          </Link>
          <Typography color="text.primary">Loja</Typography>
          <Typography color="text.primary">{store.name}</Typography>
        </Breadcrumbs>
      </Container>

      {/* Header da loja */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          color: 'white',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {store.logo && (
              <Grid item xs={12} sm={3}>
                <Box
                  component="img"
                  src={URL.createObjectURL(store.logo)}
                  alt={`Logo ${store.name}`}
                  sx={{
                    width: '100%',
                    maxWidth: 150,
                    height: 'auto',
                    borderRadius: 2,
                    border: '3px solid white',
                  }}
                />
              </Grid>
            )}
            
            <Grid item xs={12} sm={store.logo ? 9 : 12}>
              <Typography variant="h3" component="h1" gutterBottom>
                {store.name}
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                {store.description}
              </Typography>

              {/* Informações de contato */}
              <Grid container spacing={2}>
                {store.location && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" />
                      <Typography variant="body2">{store.location}</Typography>
                    </Box>
                  </Grid>
                )}
                
                {store.contactPhone && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" />
                      <Typography variant="body2">{store.contactPhone}</Typography>
                    </Box>
                  </Grid>
                )}
                
                {store.contactEmail && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      <Typography variant="body2">{store.contactEmail}</Typography>
                    </Box>
                  </Grid>
                )}
                
                {store.website && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WebsiteIcon fontSize="small" />
                      <Typography variant="body2">{store.website}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Navegação */}
      <Box sx={{ bgcolor: 'grey.100', py: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="text">Todos</Button>
              <Button variant="text">Destaques</Button>
              <Button variant="text">Promoções</Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small">
                <ShareIcon />
              </IconButton>
              <IconButton size="small">
                <FavoriteIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<CartIcon />}
                sx={{
                  bgcolor: primaryColor,
                  '&:hover': { bgcolor: secondaryColor },
                }}
              >
                Carrinho (0)
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Produtos */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Produtos ({store.products?.length || 0})
        </Typography>
        
        {store.products && store.products.length > 0 ? (
          <Grid container spacing={3}>
            {store.products.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <ProductCard product={product} primaryColor={primaryColor} secondaryColor={secondaryColor} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum produto disponível no momento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Volte em breve para ver nossos produtos
            </Typography>
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.100', py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Sobre a Loja
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {store.description}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Contato
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {store.address && (
                  <Typography variant="body2" color="text.secondary">
                    {store.address}, {store.city} - {store.state}
                  </Typography>
                )}
                {store.contactPhone && (
                  <Typography variant="body2" color="text.secondary">
                    {store.contactPhone}
                  </Typography>
                )}
                {store.contactEmail && (
                  <Typography variant="body2" color="text.secondary">
                    {store.contactEmail}
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Links Rápidos
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Termos de Uso
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Política de Privacidade
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Central de Ajuda
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © 2024 {store.name}. Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

// Componente para card de produto
const ProductCard: React.FC<{ product: ProductData; primaryColor: string; secondaryColor: string }> = ({ 
  product, 
  primaryColor,
  secondaryColor
}) => {
  const { name, description, price, originalPrice, quantity, tags, featured } = product;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', p: 2 }}>
        {/* Placeholder para imagem do produto */}
        <Box
          sx={{
            width: '100%',
            height: 200,
            bgcolor: 'grey.200',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Imagem do Produto
          </Typography>
        </Box>

        {/* Badges */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
          {featured && (
            <Chip label="Destaque" size="small" color="primary" />
          )}
          {originalPrice && originalPrice > price && (
            <Chip label="Promoção" size="small" color="error" />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 0 }}>
          <Typography variant="h6" gutterBottom noWrap>
            {name || 'Nome do Produto'}
          </Typography>
          
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, height: 40, overflow: 'hidden' }}
          >
            {description || 'Descrição do produto'}
          </Typography>

          {/* Preços */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="success.main" component="span">
              R$ {price?.toFixed(2) || '0,00'}
            </Typography>
            {originalPrice && originalPrice > price && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ textDecoration: 'line-through', ml: 1 }}
              >
                R$ {originalPrice.toFixed(2)}
              </Typography>
            )}
          </Box>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
              {tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          {/* Estoque */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Estoque: {quantity || 0}
          </Typography>
        </CardContent>

        {/* Botão de ação */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              bgcolor: primaryColor,
              '&:hover': { bgcolor: secondaryColor },
            }}
            disabled={!quantity || quantity <= 0}
          >
            {quantity && quantity > 0 ? 'Adicionar ao Carrinho' : 'Indisponível'}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default StorePublicPage;
